import { Line } from "@ant-design/plots";
import { Button, Card, Col, DatePicker, Row, Space, Spin, Table } from "antd";
import { LINE_WISE_DAILY_EFFICIENCY } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import ListTable from "common/ListTable";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { formatNumberWithCommas } from "utils/functions";

export default function Efficiency() {
  const [selectedDate, setSelectedDate] = useState(moment());
  const [data, setData] = useState({
    dataSource: [],
    barChart: [],
    loading: false,
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Fix round 1: guard against setState after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const config = {
    data: data.barChart?.map((v) => ({
      line: v.line,
      value: parseFloat(v.value), // Convert string to number
    })),
    xField: "line",
    yField: "value",
    point: {
      shapeField: "square",
      sizeField: 4,
    },
    interaction: {
      tooltip: {
        marker: false,
      },
    },
    style: {
      lineWidth: 2,
    },
  };

  const columns = [
    {
      title: "Sewing Line",
      dataIndex: "sewingLine",
      key: "sewingLine",
    },
    {
      title: "Line Chief",
      dataIndex: "lineChief",
      key: "lineChief",
    },
    {
      title: "Manpower",
      dataIndex: "manpower",
      key: "manpower",
      align: "right",
    },
    {
      title: "Production",
      dataIndex: "production",
      key: "production",
      align: "right",
    },
    {
      title: "SAM",
      dataIndex: "sam",
      key: "sam",
      align: "right",
      render: (text) => formatNumberWithCommas(parseFloat(text).toFixed(2)),
    },
    {
      title: "Working Hour",
      dataIndex: "workingHour",
      key: "workingHour",
      align: "right",
    },
    {
      title: "OT Hour Manpower",
      dataIndex: "otHourManpower",
      key: "otHourManpower",
      align: "right",
      render: (text) => parseInt(text).toLocaleString(undefined, { maximumFractionDigits: 0 }),
    },
    {
      title: "Available Minutes",
      dataIndex: "availableMinutes",
      key: "availableMinutes",
      align: "right",
      render: (text) => parseInt(text).toLocaleString(undefined, { maximumFractionDigits: 0 }),
    },
    {
      title: "Earn Minute",
      dataIndex: "earnMinute",
      key: "earnMinute",
      align: "right",
      render: (text) => parseInt(text).toLocaleString(undefined, { maximumFractionDigits: 0 }),
    },
    {
      title: "Actual Efficiency",
      dataIndex: "actualEfficiency",
      key: "actualEfficiency",
      align: "right",
      render: (text) => `${formatNumberWithCommas(parseFloat(text).toFixed(2))}%`,
    },
    {
      title: "Ranking on Efficiency",
      dataIndex: "ranking",
      key: "ranking",
      align: "center",
    },
  ];

  // ux2(A/presets): dateOverride lets the Today/Yesterday buttons fetch with
  // the just-picked date instead of the stale `selectedDate` closure value
  // (setSelectedDate is async; handleSearch would otherwise fire before the
  // state update lands).
  const handleSearch = async (dateOverride) => {
    const searchDate = dateOverride || selectedDate;
    setData({ dataSource: [], barChart: [], loading: true });
    setHasSearched(true);
    setFetchError(null);
    try {
      const query = `${LINE_WISE_DAILY_EFFICIENCY}?start_date=${searchDate.format("YYYY-MM-DD")}`;
      const response = await getData(query);

      if (!mountedRef.current) return;

      const lineWiseSortDataSource = response?.data?.data?.data?.sort((a, b) => {
        const numA = parseInt(a?.line?.match(/\d+/));
        const numB = parseInt(b?.line?.match(/\d+/));
        return numA - numB;
      });

      const dataSource = lineWiseSortDataSource.map((item, index) => {
        const lineChief = item?.line_chief?.map((chief) => chief?.line_chief);
        const uniqueLineChiefAsString = Array.from(new Set(lineChief)).join(", ");
        return {
          key: index + 1,
          sewingLine: item.line,
          lineChief: uniqueLineChiefAsString,
          manpower: item.manpower,
          production: item.actual_production,
          sam: item.sam,
          workingHour: item.working_hour,
          otHourManpower: item.ot_hour_manpower,
          availableMinutes: item.available_minute,
          earnMinute: item.earn_minute,
          actualEfficiency: item.actual_efficiency,
          ranking: item.rank,
        };
      });

      const barChart = dataSource.map((item) => {
        return {
          line: item.sewingLine,
          value: formatNumberWithCommas(parseFloat(item.actualEfficiency).toFixed(2)),
        };
      });

      setData({ dataSource: dataSource, barChart: barChart, loading: false });
    } catch (error) {
      console.log(error);
      if (!mountedRef.current) return;
      setData({ dataSource: [], barChart: [], loading: false });
      setFetchError("Could not load this list.");
    }
  };

  const handleReset = () => {
    setSelectedDate(moment());
    setData({ dataSource: [], barChart: [], loading: false });
    setHasSearched(false);
  };

  // Round 4 (T2): restored. Round 3 removed this because line-wise-daily-efficiency
  // measured 240.24s warm (22.49s cold) for today's date on a real data day
  // (T1-timings/timings.md row 19, the single slowest page in that round). Task 1's
  // set-based rewrite of LineWiseDailyEfficiencyService now answers the same seeded
  // default in 0.06-0.21s in-process and 0.15-0.17s kernel-dispatch
  // (D:\cgl-erp\patches\ux-round4\golden\timings.md), comfortably inside R4-5's
  // 5s-warm/15s-cold bar, so the mount-time fetch is safe again.
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      title={`
        Line wise Daily Efficiency Report for ${selectedDate.format("DD-MMM-YYYY")}
      `}
      bordered={false}
    >
      <br />
      <Row gutter={24}>
        <Col span={6}>
          <DatePicker
            allowClear={false}
            format={"DD-MMM-YYYY"}
            style={{
              width: "100%",
            }}
            value={selectedDate}
            onChange={handleDateChange}
          />
        </Col>
        <Col span={6}>
          <Space>
            <Button
              size="small"
              onClick={() => {
                const d = moment();
                setSelectedDate(d);
                handleSearch(d);
              }}
              disabled={data.loading}
            >
              Today
            </Button>
            <Button
              size="small"
              onClick={() => {
                const d = moment().subtract(1, "days");
                setSelectedDate(d);
                handleSearch(d);
              }}
              disabled={data.loading}
            >
              Yesterday
            </Button>
            <Button type="primary" danger onClick={handleReset} disabled={data.loading}>
              Reset
            </Button>
            <Button type="primary" onClick={() => handleSearch()} loading={data.loading} disabled={data.loading}>
              Search
            </Button>
          </Space>
        </Col>
      </Row>

      {hasSearched && (
        <>
          <br />
          <ListTable
            dataSource={data.dataSource}
            columns={columns}
            pagination={false}
            bordered
            loading={data.loading}
            error={fetchError}
            onRetry={() => handleSearch()}
            locale={{
              emptyText: `No records for ${selectedDate.format("DD-MMM-YYYY")}. Change the date above.`,
            }}
            summary={(tableData) => {
              if (tableData.length === 0) return null;
              let totalManpower = 0;
              let totalProduction = 0;
              let totalSam = 0;
              let totalWorkingHour = 0;
              let totalOtHourManpower = 0;
              let totalAvailableMinutes = 0;
              let totalEarnMinute = 0;
              let totalActualEfficiency = 0;

              tableData.forEach((item) => {
                totalManpower += item.manpower;
                totalProduction += item.production;
                totalSam += parseFloat(item.sam);
                totalWorkingHour += item.workingHour;
                totalOtHourManpower += item.otHourManpower;
                totalAvailableMinutes += item.availableMinutes;
                totalEarnMinute += item.earnMinute;
                totalActualEfficiency += parseFloat(item.actualEfficiency);
              });

              return (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={2}>
                      <strong>Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <strong>{totalManpower}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <strong>{totalProduction}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <strong>{formatNumberWithCommas(parseFloat(totalSam).toFixed(2))}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <strong>{totalWorkingHour}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <strong>{totalOtHourManpower}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <strong>{totalAvailableMinutes}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <strong>{totalEarnMinute}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <strong>{formatNumberWithCommas(parseFloat(totalActualEfficiency).toFixed(2))}%</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell></Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              );
            }}
          />
          <div
            style={{
              padding: "20px",
              backgroundColor: "#ffffff",
              borderRadius: "10px",
            }}
          >
            <h3
              style={{
                color: "#00bfff",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              Today's Est. Net Profit/taka
            </h3>
            {data.loading ? (
              <div style={{ textAlign: "center", padding: "50px" }}>
                <Spin size="large" />
              </div>
            ) : (
              <Line {...config} />
            )}
          </div>
        </>
      )}
    </Card>
  );
}
